import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useWearablePathAwareDefaults from "@/hooks/useWearablePathAwareDefaults";
import useWearablePathOverrideMemory from "@/hooks/useWearablePathOverrideMemory";
import {
  recordIgnoredRecommendation,
  recordRecommendationExposure,
  recordRecommendationOverride,
  recordUserChoice,
} from "@/lib/wearables/wearablesBehaviorStore";
import {
  buildWearablesContinuityEntry,
  buildWearablesRouteCompanionModel,
  resolveWearablesRouteSurface,
} from "@/lib/wearables/wearables.routeCompanion";
import { recordTrajectory } from "@/lib/wearables/wearablesTrajectoryStore";
import { useWearablesStore } from "@/state/wearables/useWearablesStore";

import WearablesContinuityCapsule from "./WearablesContinuityCapsule";
import WearablesInlineSignal from "./WearablesInlineSignal";
import WearablesIntercept from "./WearablesIntercept";
import WearablesMiniOverlay from "./WearablesMiniOverlay";
import WearablesResumeTray from "./WearablesResumeTray";
import "./wearables-v2.css";

function buildFocusSearch(track: string): string {
  const searchParams = new URLSearchParams();
  searchParams.set("entry", "wearables");
  searchParams.set("focus", track);
  return `?${searchParams.toString()}`;
}

export default function WearablesRouteCompanion(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();

  const baseDefaults = useWearablePathAwareDefaults();
  const continuity = useWearablesStore((state) => state.continuity);
  const launchActivation = useWearablesStore((state) => state.launchActivation);
  const setLaunchActivationStage = useWearablesStore(
    (state) => state.setLaunchActivationStage,
  );
  const setContinuityEntry = useWearablesStore((state) => state.setContinuityEntry);

  const surfaceMeta = React.useMemo(
    () => resolveWearablesRouteSurface(location.pathname),
    [location.pathname],
  );
  const hookSurface = surfaceMeta?.surface ?? "guided-studio";
  const { overrideEntry, pinTrack, dismiss, reset } = useWearablePathOverrideMemory(hookSurface);

  const currentEntry = surfaceMeta ? continuity?.[surfaceMeta.surface] ?? null : null;

  const model = React.useMemo(
    () =>
      buildWearablesRouteCompanionModel({
        pathname: location.pathname,
        search: location.search,
        baseDefaults,
        overrideEntry,
        continuityEntry: currentEntry,
      }),
    [baseDefaults, currentEntry, location.pathname, location.search, overrideEntry],
  );

  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const lastAutoOpenKey = React.useRef<string | null>(null);
  const lastExposureKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!model) {
      setOverlayOpen(false);
      lastAutoOpenKey.current = null;
      return;
    }

    const nextKey = `${model.surface}:${location.pathname}:${location.search}`;
    if (model.shouldInterrupt && lastAutoOpenKey.current !== nextKey) {
      setOverlayOpen(true);
      lastAutoOpenKey.current = nextKey;
    }
  }, [location.pathname, location.search, model]);

  React.useEffect(() => {
    if (!model) {
      lastExposureKey.current = null;
      return;
    }

    const nextKey = `${model.surface}:${model.effectiveTrack}:${location.pathname}:${location.search}`;
    if (lastExposureKey.current === nextKey) {
      return;
    }

    lastExposureKey.current = nextKey;
    recordTrajectory(`surface:${model.surface}`);

    if (!model.fromWearables) {
      recordRecommendationExposure();
    }
  }, [location.pathname, location.search, model]);

  React.useEffect(() => {
    if (!model || !launchActivation || !model.fromWearables) {
      return;
    }

    if (launchActivation.stage !== "activated") {
      setLaunchActivationStage("activated");
    }

    setContinuityEntry(
      model.surface,
      buildWearablesContinuityEntry(model, "launched"),
    );
    recordTrajectory(`mode:${model.effectiveTrack}`);
  }, [launchActivation, model, setContinuityEntry, setLaunchActivationStage]);

  const recentEntries = React.useMemo(
    () =>
      Object.values(continuity ?? {})
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
        .filter((entry) => entry.surface !== surfaceMeta?.surface)
        .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))
        .slice(0, 2),
    [continuity, surfaceMeta?.surface],
  );
  const readinessStatusLabel = React.useMemo(
    () => (model ? buildReadinessStatusLabel(model.signalTrack) : "Readiness"),
    [model],
  );

  const handleAccept = React.useCallback(() => {
    if (!model) {
      return;
    }

    if (model.effectiveTrack === "general") {
      reset();
    } else {
      pinTrack(model.effectiveTrack);
    }

    recordUserChoice({
      mode: model.effectiveTrack,
      timeOfDay: model.context.timeOfDay,
      sessionType: model.context.sessionType,
    });
    recordTrajectory(`mode:${model.effectiveTrack}`);

    setContinuityEntry(
      model.surface,
      buildWearablesContinuityEntry(
        model,
        currentEntry ? "resumed" : "accepted",
      ),
    );

    setOverlayOpen(false);
    navigate(
      {
        pathname: model.destinationHref,
        search: buildFocusSearch(model.effectiveTrack),
      },
      { replace: true },
    );
  }, [currentEntry, model, navigate, pinTrack, reset, setContinuityEntry]);

  const handleRevise = React.useCallback(() => {
    if (!model) {
      return;
    }

    recordRecommendationOverride();
    recordTrajectory("action:revise");
    setContinuityEntry(
      model.surface,
      buildWearablesContinuityEntry(model, "suggested"),
    );
    setOverlayOpen(false);
    navigate(`/wearables/connect?track=${model.effectiveTrack}&sourceSurface=${model.surface}`);
  }, [model, navigate, setContinuityEntry]);

  const handleDismiss = React.useCallback(() => {
    if (!model) {
      return;
    }

    recordIgnoredRecommendation();
    recordTrajectory("action:dismiss");
    dismiss();
    setContinuityEntry(
      model.surface,
      buildWearablesContinuityEntry(model, "dismissed"),
    );
    setOverlayOpen(false);
  }, [dismiss, model, setContinuityEntry]);

  const handleResumeEntry = React.useCallback(
    (entry: NonNullable<(typeof recentEntries)[number]>) => {
      recordTrajectory(`surface:${entry.surface}`);
      navigate(`${entry.href}${buildFocusSearch(entry.effectiveTrack)}`);
    },
    [navigate],
  );

  if (!model && recentEntries.length === 0) {
    return null;
  }

  return (
    <>
      {model ? (
        <WearablesInlineSignal passive={model.fromWearables}>
          {model.fromWearables ? (
            <WearablesContinuityCapsule
              statusLabel={readinessStatusLabel}
              label={model.label}
              track={model.effectiveTrack}
              body={model.body}
              overlayOpen={overlayOpen}
              onToggleDetails={() => setOverlayOpen((open) => !open)}
            />
          ) : (
            <WearablesIntercept
              statusLabel={readinessStatusLabel}
              title={model.title}
              body={model.body}
              chips={model.inlineChips}
              currentEntry={Boolean(currentEntry)}
              destinationLabel={model.destinationLabel}
              destinationSurface={model.destinationSurface}
              overlayOpen={overlayOpen}
              onAccept={handleAccept}
              onRevise={handleRevise}
              onToggleDetails={() => setOverlayOpen((open) => !open)}
            />
          )}
        </WearablesInlineSignal>
      ) : null}

      {model && overlayOpen ? (
        <WearablesMiniOverlay
          title={model.title}
          body={model.body}
          chips={model.continuityChips}
          destinationSurface={model.destinationSurface}
          destinationLabel={model.destinationLabel}
          adaptiveReason={model.adaptiveReason}
          confidence={model.confidence}
          signalTrack={model.signalTrack}
          context={model.context}
          onAccept={handleAccept}
          onRevise={handleRevise}
          onDismiss={handleDismiss}
        />
      ) : null}

      {recentEntries.length ? (
        <WearablesResumeTray entries={recentEntries} onResumeEntry={handleResumeEntry} />
      ) : null}
    </>
  );
}

function buildReadinessStatusLabel(track: string): string {
  if (track === "xr") return "XR ready";
  if (track === "creator") return "Creator ready";
  if (track === "readiness") return "Readiness ready";
  if (track === "recovery") return "Recovery ready";
  return "General ready";
}